import { useEffect, useRef, useState } from 'react';
import useIsomorphicEffect from '../../../utilities/useIsomorphicEffect';

interface AnimateHeightProps {
  children: React.ReactNode;
  expanded?: boolean;
  duration?: number;
}

export const AnimateHeight = ({
  children,
  expanded = false,
  duration = 400,
}: AnimateHeightProps) => {
  const [state, setState] = useState({
    height: expanded ? 'auto' : 0,
    overflow: expanded ? 'visible' : 'hidden',
    display: expanded ? 'block' : 'none',
  });
  const innerElement = useRef(null);
  const { height, overflow, display } = state;

  useIsomorphicEffect(() => {
    if (innerElement.current) {
      setState((currentState) => ({
        ...currentState,
        height:
          !expanded && !!innerElement.current
            ? innerElement.current['offsetHeight']
            : 0,
        overflow: 'hidden',
        display: 'block',
      }));
    }
  }, [expanded]);

  useEffect(() => {
    const renderInnerElementBeforeSettingHeight = setTimeout(() => {
      setState((currentState) => {
        return {
          ...currentState,
          height:
            expanded && !!innerElement.current
              ? innerElement.current['offsetHeight']
              : 0,
        };
      });
    }, 0);

    const setAccessibilityAttributesAfterAnimation = setTimeout(() => {
      setState((currentState) => {
        return {
          ...currentState,
          height: expanded ? 'auto' : currentState.height,
          overflow: expanded ? 'visible' : 'hidden',
          display: expanded ? 'block' : 'none',
        };
      });
    }, duration);

    return () => {
      clearTimeout(renderInnerElementBeforeSettingHeight);
      clearTimeout(setAccessibilityAttributesAfterAnimation);
    };
  }, [duration, expanded]);

  return (
    <div
      aria-hidden={!expanded}
      style={{
        height,
        overflow,
        transition: `height ${duration}ms ease-in-out`,
      }}
    >
      <div style={{ display }} ref={innerElement}>
        {display !== 'none' && children}
      </div>
    </div>
  );
};
